import { useRouter } from '@tanstack/react-router';
import { HUBS, hubEnter } from './hubs.js';
import { type GatedNavGroup } from './permissions.js';

/**
 * Rail navigation — SIX HUBS AND A QUIET FOOT (owner master UX/IA directive +
 * Phase A navigation ruling; doc 06 §6.5 still governs the mechanics).
 *
 *   SIDEBAR              = HUB          (this file)
 *   TOP NAVIGATION CARDS = MAJOR DESTINATIONS INSIDE THE HUB (`hubs.ts`,
 *                          rendered by `shell/HubCards.tsx`)
 *   PAGE BELOW           = ACTUAL WORK SURFACE (routes, untouched)
 *
 * The rail used to BE the site map — seven groups, twenty-odd items — and the
 * directive's finding was that a rail that lists everything explains nothing.
 * The map of what exists moved to `hubs.ts`; this file is only the six doors
 * plus the admin foot.
 *
 * WHAT DID NOT CHANGE, because it is the part that was hard-won:
 *
 *   ROLE-AWARENESS IS A CONSEQUENCE, NOT A LIST. A hub entry resolves per
 *   principal (`GatedNavItem.enter`): visible when any destination inside is
 *   openable, landing on the first one that is. Every destination keeps the
 *   permission its screen's own guard was argued from — see `hubs.ts` and
 *   `gate-keys.test.ts` — so a supervisor and an org_admin still get different
 *   products without anyone maintaining a per-role menu, no entry is offered
 *   that can only 403, and no held capability is hidden.
 *
 *   EVERY ROUTE STILL EXISTS. Demotion from the rail is not removal from the
 *   product: hub cards, contextual links, and global Find carry what the rail
 *   used to, and `route-reachability.test.ts` keeps proving it click by click.
 */
export const NAV_MAP: GatedNavGroup[] = [
  {
    // No label: six hubs need no header over them — the rail IS the hubs.
    key: 'hubs',
    label: '',
    items: HUBS.map((hub) => ({
      key: hub.key,
      label: hub.label,
      icon: hub.icon,
      // The static face href is the entry's identity (tests, titles); the
      // rendered href comes from `enter` resolution per principal.
      href: hub.face.href,
      enter: hubEnter(hub),
      // The sidebar accordion: the hub's full map — cards AND secondary
      // destinations (Location history), each gated as in hubs.ts. The top
      // strip deliberately shows less than this (directive §5).
      childItems: [...hub.cards, ...(hub.more ?? [])],
    })),
  },
  {
    /**
     * The quiet admin foot, and it stays EXTREMELY RESTRAINED (founder Phase A
     * review §4): Integrations · Settings · Your profile, nothing else.
     * Pinned to the rail's bottom by `foot: true` — administration is reached
     * deliberately, not scanned past on the way to work.
     *
     * Settings stays ungated on purpose (the centre shows the sections this
     * principal may read — an honest empty state beats a vanished entry).
     * Roles & permissions and the Exports catalog both live inside it now
     * (`settingsNav.ts` carries each entry with its own gate) — a route does
     * not earn rail real estate just because it exists.
     */
    key: 'foot',
    label: '',
    foot: true,
    items: [
      {
        key: 'integrations',
        label: 'Integrations',
        icon: 'link',
        href: '/settings/records',
        permission: 'integrations:read',
      },
      { key: 'settings', label: 'Settings', icon: 'setup', href: '/settings' },
      { key: 'me-profile', label: 'Your profile', icon: 'person', href: '/me/profile' },
    ],
  },
];

/**
 * Imperative navigation that keeps URL-as-truth (doc 06 §6.1).
 *
 * The router matches `to` against the ROUTE PATH only, so an href carrying a
 * query string ("/time/timesheets/abc?weekOf=…") matched nothing and the
 * navigation silently did nothing. Splitting the two lets a caller pass a plain
 * href — which is what every call site was already doing — and still land on the
 * right screen with its search intact.
 */
export function useNav(): (href: string) => void {
  const router = useRouter();
  return (href: string) => {
    const q = href.indexOf('?');
    const path = q === -1 ? href : href.slice(0, q);
    const search = q === -1 ? null : Object.fromEntries(new URLSearchParams(href.slice(q + 1)));
    void router.navigate(search ? { to: path, search } : { to: path });
  };
}
